import React, { createContext, useState, useContext, useEffect } from "react";
import { User, getUserFromUsername } from "../../services/UserServices";
import {
  checkIsFollowing,
  followUser,
  getFollows,
  getNbFollowers,
  getNbFollows,
  unfollowUser,
} from "../../services/FollowServices";
// import { useLocation } from "react-router-dom";

interface Props {
  user: User | null;
  nbFollows: number;
  nbFollowers: number;
  fetchUser: (username: string) => void;
  follow: (id: number) => void;
  unfollow: (id: number) => void;
  follows: { id: number; username: string }[];
  setFollowsPage: React.Dispatch<React.SetStateAction<number>>;
  hasMoreFollows: boolean;
  isFollowing: boolean;
}

const FetchedUser = createContext<Props | undefined>(undefined);

export const FetchedUserProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [nbFollowers, setNbFollowers] = useState<number>(0);
  const [nbFollows, setNbFollows] = useState<number>(0);
  const [follows, setFollows] = useState<{ id: number; username: string }[]>(
    []
  );
  const [followsPage, setFollowsPage] = useState<number>(0);
  const [hasMoreFollows, setHasMoreFollows] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const limit = 16;

  const fetchUser = (username: string) => {
    if (!username) {
      return;
    }

    const func = async () => {
      try {
        const fetchUser = await getUserFromUsername(username);
        setUser(fetchUser);
        const nbFollowers = await getNbFollowers(fetchUser.id);
        const nbFollows = await getNbFollows(fetchUser.id);
        setNbFollowers(nbFollowers);
        setNbFollows(nbFollows);
        const res = await checkIsFollowing(fetchUser.id);
        setIsFollowing(res);
      } catch (err) {
        console.log("Failed to fetch follows");
      }
    };

    // if (username !== auth.user?.username) {
    func();
    // }
    // else {
    //   setUser(auth.user);
    // }
  };

  useEffect(() => {
    setFollowsPage(0);
    setHasMoreFollows(true);
    setFollows([]);
  }, [user]);

  useEffect(() => {
    const fetchFollows = async () => {
      if (loading || !hasMoreFollows || !user || followsPage === 0) return;

      setLoading(true);
      try {
        const offset = (followsPage - 1) * limit;
        const fetched = await getFollows(user.id, limit, offset);

        if (fetched.followsList.length < limit) {
          setHasMoreFollows(false);
        }

        setFollows((prevFollows) => {
          const newFollows = fetched.followsList.filter(
            (f) => !prevFollows.some((pf) => pf.id === f.id)
          );
          return [...prevFollows, ...newFollows];
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFollows();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const follow = async (id: number) => {
    try {
      await followUser(id);
      setIsFollowing(true);
      setNbFollowers((prev) => Number(prev) + 1);
    } catch (err) {}
  };

  const unfollow = async (id: number) => {
    try {
      await unfollowUser(id);
      setIsFollowing(false);
      setNbFollowers(nbFollowers - 1);
    } catch (err) {
      console.log("Failed to follow user");
    }
  };

  return (
    <FetchedUser.Provider
      value={{
        user,
        nbFollows,
        nbFollowers,
        fetchUser,
        follow,
        unfollow,
        follows,
        setFollowsPage,
        hasMoreFollows,
        isFollowing,
      }}
    >
      {children}
    </FetchedUser.Provider>
  );
};

export const useFetchedUserContext = () => {
  const context = useContext(FetchedUser);
  if (!context) {
    throw new Error("use must be used within a FetchedUserProvider");
  }
  return context;
};
